<template>
  <simple-list
    ref="menuList"
    :server-items="items"
    query="sectionId"
    :variant="'menu-list-' + columns"
  >
    <template
      v-if="selectedSectionData"
      #banner
    >
      <div
        v-if="bannerImageExist && selectedSectionData.title"
        class="banner-wrapper"
      >
        <div class="banner-image">
          <picture
            :key="`banner-image-${selectedSectionData.id}`"
            v-lazyload="{ src: selectedSectionData.imageSrc,
                          variant: 'link-highlight' }"
            class="image-wrapper"
          >
            <img
              class="image"
              src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
              :alt="selectedSectionData.imageAlt"
            >
          </picture>
        </div>
        <div class="banner-content">
          <div class="banner-title">
            {{ selectedSectionData.title }}
          </div>
          <div
            class="banner-description"
            v-html="selectedSectionData.description"
          />
        </div>
      </div>
    </template>
    <template #filter>
      <accessible-tabs>
        <div
          v-if="headerTabVisibility"
          ref="menuListHeader"
          class="menu-list-header"
          :class="{ 'carousel-active': showCarousel }"
        >
          <carousel
            v-if="showCarousel"
            :key="'carousel-' + sections.length"
            selector=".carousel-wrapper"
            variant="menu-tabs"
          >
            <div class="carousel">
              <div
                ref="menuListHeaderCarousel"
                role="tablist"
                class="filter-items carousel-wrapper"
              >
                <button
                  v-for="(section, i) in sections"
                  :id="'menu-filter-' + i"
                  :key="section.id"
                  type="button"
                  :aria-controls="section.label"
                  :tabindex="selectedSectionId == section.id ? '0' : '-1'"
                  class="filter-item tab-group-button"
                  :class="[
                    {
                      active: selectedSectionId === section.id
                    },
                  ]"
                  role="tab"
                  :aria-selected="selectedSectionId === section.id"
                  @click="filterSection($event, section.id)"
                  @keydown.right.prevent="
                    filterSection(
                      $event,
                      i === sections.length - 1 ? null : sections[i + 1].id
                    )
                  "
                  @keydown.left.prevent="
                    filterSection(
                      $event,
                      i === 0 ? null : sections[i - 1].id
                    )
                  "
                >
                  {{ section.label }}
                </button>
              </div>
            </div>
          </carousel>
          <div
            v-if="!showCarousel"
            role="tablist"
            class="filter-items"
          >
            <button
              v-for="(section, i) in sections"
              :id="'menu-filter-' + i"
              :key="section.id"
              type="button"
              :tabindex="selectedSectionId == section.id ? '0' : '-1'"
              :aria-controls="section.label"
              class="filter-item tab-group-button"
              :class="[
                {
                  active: selectedSectionId === section.id
                },
              ]"
              role="tab"
              :aria-selected="selectedSectionId === section.id"
              @click="filterSection($event, section.id)"
              @keydown.right.prevent="
                filterSection(
                  $event,
                  i === sections.length - 1 ? null : sections[i + 1].id
                )
              "
              @keydown.left.prevent="
                filterSection(
                  $event,
                  i === 0 ? null : sections[i - 1].id
                )
              "
            >
              {{ section.label }}
            </button>
          </div>
        </div>
      </accessible-tabs>
    </template>
    <template #rendered-list="{ items }">
      <ul>
        <li
          v-for="item in items"
          :key="item.id"
        >
          <menu-card
            :key="item.id"
            :menu-item="item"
            :grid-columns="parseInt(columns)"
          />
        </li>
      </ul>
    </template>
  </simple-list>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import MenuCard from '@components/01.base/menu/menu-card.vue'
  import AccessibleTabs from '../accessible-tabs/accessible-tabs'

  export default {
    name: 'MenuList',
    components: {
      MenuCard,
      AccessibleTabs
    },

    props: {
      menuData: {
        type: Array,
        required: true,
        default: () => ([])
      },
      items: {
        type: Array,
        required: true,
        default: () => ([])
      },
      sections: {
        type: Array,
        default: () => ([])
      },
    },

    data() {
      return {
        selectedSectionId: null,
        columns: 2,
        showCarousel: false,
        emptyScene7ImageSrc: 'https://photos.mandarinoriental.com/is/image',
      }
    },

    mounted() {
      const firstSectionId = this.menuData[0].id
      this.availableExperiences = this.experiences
      this.updateSection({ sectionId: firstSectionId })
      this.$nextTick(() => {
        this.isCarouselEnable()
      })
    },
    computed: {
      ...mapState('responsive', { wWidth: 'width', wHeight: 'height' }),
      ...mapGetters('i18n', ['tc']),

      selectedSectionData() {
        const sectionData = this.menuData.filter((item) => item.id === this.selectedSectionId)
        return sectionData.length > 0 ? sectionData[0] : null
      },

      headerTabVisibility() {
        return this.sections.length > 1
      },

      bannerImageExist() {
        return this.selectedSectionData.imageSrc && this.selectedSectionData.imageSrc !== this.emptyScene7ImageSrc
      }
    },
    watch: {
      wWidth() {
        this.isCarouselEnable()
      }
    },
    methods: {
      updateSection(filter) {
        this.$refs.menuList.updateFilter(filter)
        this.selectedSectionId = filter.sectionId
        this.columns = this.getSectionColumns(filter.sectionId)
      },

      filterSection(e, sectionId) {
        e.preventDefault()
        if (sectionId) {
          this.updateSection({ sectionId })
        } else if (!sectionId) {
          this.updateSection({})
        }
      },

      getSectionColumns(sectionId) {
        const selectedSection = this.sections.find((section) => section.id === sectionId)
        return selectedSection ? selectedSection.columns : 2
      },

      isCarouselEnable() {
        if (this.$refs.menuListHeader.offsetWidth) {
          const menuListHeaderWidth = this.$refs.menuListHeader.offsetWidth
          const filterItems = document.querySelectorAll('.filter-item')
          let itemsWidth = 0
          filterItems.forEach((item) => {
            itemsWidth += item.offsetWidth
          })
          itemsWidth += 24 * filterItems.length - 12
          this.showCarousel = itemsWidth > menuListHeaderWidth
        }
      }
    }
  }
</script>
